import type {
  CmmdBoardTask,
  CmmdMember,
  CmmdProjectAgentMember,
  CmmdProjectMilestone,
  CmmdTaskWorkflow,
  CreateCmmdBoardTaskParams,
  CmmdTaskStatus,
} from "@cmmd-forge/contracts";

import { getStatusConfig } from "~/components/command-center/statusConfig";
import { WORK_ITEM_TYPE_OPTIONS, taskWorkItemTypeLabel } from "./atoms/TaskWorkItemTypeIcon";
import type { ProjectTaskPickerOption } from "./ProjectTaskPicker";
import { INTERRUPTED_TASK_STATUSES } from "./taskWorkflowGroups";
import { TASK_STATUS_OPTIONS } from "./taskViewTypes";

export type ProjectTaskCreatePriority = NonNullable<CreateCmmdBoardTaskParams["priority"]>;

export interface ProjectTaskCreateStatusSelection {
  readonly status: CmmdTaskStatus;
  readonly workflowStageId: string | null;
}

interface ProjectTaskCreateStatusTarget {
  readonly status: CmmdTaskStatus;
  readonly workflowStageId?: string | null;
}

export function canQuickCreateInWorkflowGroup(
  workflow: CmmdTaskWorkflow | null | undefined,
  groupId: string,
  mutationDisabled = false,
): boolean {
  return (
    !mutationDisabled &&
    (workflow == null ||
      workflow.stages.some((stage) => stage.id === groupId) ||
      INTERRUPTED_TASK_STATUSES.some((status) => status === groupId))
  );
}

export function initialCreateStatusSelection(
  workflow: CmmdTaskWorkflow | null | undefined,
  target?: ProjectTaskCreateStatusTarget | null,
): ProjectTaskCreateStatusSelection {
  if (workflow == null) {
    return {
      status: target?.status ?? "todo",
      workflowStageId: target?.workflowStageId ?? null,
    };
  }
  if (target != null && INTERRUPTED_TASK_STATUSES.some((status) => status === target.status)) {
    return { status: target.status, workflowStageId: null };
  }
  const exactStage = workflow.stages.find((stage) => stage.id === target?.workflowStageId);
  const categoryStage = workflow.stages.find(
    (stage) => stage.category === (target?.status ?? "todo"),
  );
  const stage = exactStage ?? categoryStage ?? workflow.stages[0];
  return stage == null
    ? { status: target?.status ?? "todo", workflowStageId: null }
    : { status: stage.category, workflowStageId: stage.id };
}

const PRIORITY_OPTIONS: readonly ProjectTaskCreatePriority[] = ["low", "medium", "high", "urgent"];

export function createStatusOptions(
  workflow?: CmmdTaskWorkflow | null,
): readonly ProjectTaskPickerOption[] {
  if (workflow != null) {
    return [
      ...workflow.stages.map((stage) => ({
        value: stage.id,
        label: stage.name,
        visual: { kind: "workflowStage" as const, color: stage.color },
      })),
      ...INTERRUPTED_TASK_STATUSES.map((status) => ({
        value: status,
        label: getStatusConfig(status).label,
        visual: { kind: "boardStatus" as const, status },
      })),
    ];
  }
  return TASK_STATUS_OPTIONS.map((status) => ({
    value: status,
    label: getStatusConfig(status).label,
    visual: { kind: "boardStatus", status },
  }));
}

export function createPriorityOptions(): readonly ProjectTaskPickerOption[] {
  return PRIORITY_OPTIONS.map((priority) => ({
    value: priority,
    label: priority.charAt(0).toUpperCase() + priority.slice(1),
    visual: { kind: "priority", priority },
  }));
}

export function createAssigneeOptions(
  members: readonly CmmdMember[],
  agentMembers: readonly CmmdProjectAgentMember[] = [],
): readonly ProjectTaskPickerOption[] {
  return [
    ...members.map((member) => ({
      value: `human:${member.id}`,
      label: member.name,
      group: "People",
      visual: { kind: "avatar" as const, name: member.name, src: member.avatarUrl },
    })),
    ...agentMembers.map((member) => ({
      value: `agent:${member.id}`,
      label: `${member.displayName} · ${member.agentId}`,
      group: member.lifecycleStatus === "active" ? "Agent Members" : "Inactive Agent Members",
      disabled: member.lifecycleStatus !== "active",
      visual: { kind: "agent" as const, agentKind: member.agentKind },
    })),
  ];
}

export function createMilestoneOptions(
  milestones: readonly CmmdProjectMilestone[],
): readonly ProjectTaskPickerOption[] {
  return milestones.map((milestone) => ({
    value: String(milestone.id),
    label: milestone.title,
    visual: { kind: "milestone", status: milestone.status },
  }));
}

export function createTaskOptions(
  tasks: readonly CmmdBoardTask[],
): readonly ProjectTaskPickerOption[] {
  return tasks.map((task) => ({
    value: String(task.id),
    label: `#${task.id} ${task.title}`,
    visual: { kind: "task" },
  }));
}

export function createWorkItemTypeOptions(): readonly ProjectTaskPickerOption[] {
  return WORK_ITEM_TYPE_OPTIONS.map((type) => ({
    value: type,
    label: taskWorkItemTypeLabel(type),
    visual: { kind: "workItemType", workItemType: type },
  }));
}

export function createTaskLabel(id: number, tasks: readonly CmmdBoardTask[]): string {
  const task = tasks.find((candidate) => candidate.id === id);
  return task === undefined ? `#${id}` : `#${id} ${task.title}`;
}

export function optionalTaskId(value: string | null): number | null {
  return value === null ? null : Number(value);
}

export function taskIdValue(value: number | null): string | null {
  return value === null ? null : String(value);
}
